// /////////////////////////////
// // Spreadsheet tabular view data types

import {
  PropertyDescriptor,
  PropertyDescriptorType,
  Data,
  SpreadsheetConfig,
  PropertySet,
  ChoicePropertyDescriptor,
  KeyPropertyDescriptor,
  ObjectKey,
  DataSet,
} from "datasheets/model/core/Workbook";
import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import {
  datasetsAtom,
  selectedViewAtom,
} from "datasheets/model/atoms/WorkbookFocusAtoms";
import { getExplicitValue } from "datasheets/model/logic/getRowValue";
import { WritableAtom, atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ReactNode } from "react";
import DataGrid, {
  RowRendererProps,
  SortColumn,
  SortDirection,
  Column,
  textEditor,
  RowsChangeData,
  HeaderRendererProps,
} from "react-data-grid";
import { RowFilter, filterRowUsingConfig } from "./RowFilter";
import { filterDictByValue } from "datasheets/model/utils/dictionaryUtils";
import React from "react";
import { ChoiceFormatter, KeyFormatter } from "./DropDownFormatter";
import { uuid } from "datasheets/model/utils/uuid";

export interface Row extends Record<string, any> {
  rowId: number;
  objectKey: ObjectKey;
}

export type DSColumn = Column<Row, Row>;

export type ColumnMapping = { [column: number]: string };
export type ColumnDisplayProperties = { width: number };
type ColumnDisplayMapping = { [column: number]: ColumnDisplayProperties };
export interface TabularDataConfig {
  columnMapping: ColumnMapping;
  columnDisplayProperties: ColumnDisplayMapping;
  datasetId?: string;
  rowFilter?: RowFilter;
}

export function defaultColumnDisplayProperties(): ColumnDisplayProperties {
  return { width: 50 };
}

// Defaults
const defaultsAtom = atom<SpreadsheetConfig>({
  defaultColumnWidth: 150,
  defaultRowHeight: 25,
});

const offsetColumnAtom = atom(0);

type MaybeSortColumns = SortColumn[] | undefined;
const sortColumnMapAtom = atom<{ [viewKey: string]: MaybeSortColumns }>({});
const sortColumnAtom = atom<MaybeSortColumns, MaybeSortColumns>(
  (get) => {
    const sortColumnMap = get(sortColumnMapAtom);
    const currentViewKey = get(selectedViewAtom);
    return sortColumnMap[currentViewKey];
  },
  (get, set, sortColumns) => {
    const sortColumnMap = get(sortColumnMapAtom);
    const currentViewKey = get(selectedViewAtom);
    const newSortColumnMap = {
      ...sortColumnMap,
      ...{ [currentViewKey]: sortColumns },
    };
    set(sortColumnMapAtom, newSortColumnMap);
  }
);

export const setColumnWidthAtom = atom(
  null,
  (
    get,
    set,
    {
      idx,
      width,
      tabularConfigAtom,
    }: { idx: number; width: number; tabularConfigAtom: TabularConfigAtomType }
  ) => {
    set(tabularConfigAtom, (viewConfig: TabularDataConfig) => {
      if (!(idx in viewConfig.columnDisplayProperties)) {
        viewConfig.columnDisplayProperties[idx] =
          defaultColumnDisplayProperties();
      }
      viewConfig.columnDisplayProperties[idx].width = width;
      return { ...viewConfig };
    });
  }
);

type HeaderRendererGenerator = (
  columnId: number,
  properties: PropertySet
) => <R, SR>(props: HeaderRendererProps<R, SR>) => ReactNode;

const generateColumns = (
  width: number,
  offsetColumns: number,
  fixedColumns: boolean,
  viewConfig: TabularDataConfig,
  properties: PropertySet,
  config: SpreadsheetConfig,
  headerRenderer?: HeaderRendererGenerator
): DSColumn[] => {
  const numColumns = fixedColumns
    ? Object.values(viewConfig.columnMapping).length
    : 2 * Math.trunc(width / config.defaultColumnWidth);
  let columns: DSColumn[] = [{ key: "RowIndices", name: "", width: 50 }];
  for (let i = 1; i <= numColumns + offsetColumns; i++) {
    const width =
      i in viewConfig.columnDisplayProperties
        ? viewConfig.columnDisplayProperties[i].width
        : config.defaultColumnWidth;
    const propertyKey =
      i in viewConfig.columnMapping ? viewConfig.columnMapping[i] : null;
    const columnProperties = propertyKey
      ? properties.descriptors[propertyKey]
      : undefined;
    const columnKey = i.toString();
    const columnName = propertyKey
      ? properties.keyToName[propertyKey]
      : i.toString();
    // TODO: replace type check with polymorphism
    const editingEnabled: boolean =
      columnProperties?.type !== PropertyDescriptorType.Formula &&
      columnProperties?.type !== PropertyDescriptorType.Lookup;
    columns.push({
      key: columnKey,
      name: columnName,
      width: width,
      //editor: textEditor,
      headerRenderer: headerRenderer
        ? headerRenderer(i, properties)
        : undefined,
      editable: editingEnabled,
      resizable: true,
      sortable: true,
      editor: editorForType(columnProperties),
    });
  }
  return columns;
};

const editorForType = (columnProperties?: PropertyDescriptor) => {
  const type = columnProperties?.type;
  switch (type) {
    case PropertyDescriptorType.Choice:
      const options = (columnProperties as ChoicePropertyDescriptor).options;
      return ChoiceFormatter(options);
    case PropertyDescriptorType.Key:
      const keyDescriptor = columnProperties as KeyPropertyDescriptor;
      return KeyFormatter(
        keyDescriptor.referenceSheet,
        keyDescriptor.optionsColumn
      );
    default:
      return textEditor;
  }
};
const calcNumRows = (height: number, config: SpreadsheetConfig) => {
  return Math.trunc(height / config.defaultRowHeight);
};

const startRowAtom = atom(0);

const generateRows = (
  height: number,
  startRow: number,
  columns: DSColumn[],
  sortColumns: MaybeSortColumns,
  viewConfig: TabularDataConfig,
  dataset: DataSet,
  config: SpreadsheetConfig,
  data: Data,
  variables?: { [key: string]: string }
): Row[] => {
  const numRows = calcNumRows(height, config);
  // Get mapping of row number to object key, taking sort and filter into account
  const properties = dataset.properties;
  let rowMapping: (i: number) => ObjectKey | undefined = getRowMapping(
    sortColumns,
    viewConfig,
    properties,
    data,
    variables,
    dataset.naturalOrder
  );
  const rows: Row[] = [];
  for (let i = 0; i < startRow + numRows; i++) {
    const objectKey = rowMapping(i);
    let row = { rowId: i, objectKey: objectKey ?? uuid() };
    columns.forEach((column) => {
      if (column.key === "RowIndices") {
        Object.defineProperty(row, "RowIndices", {
          value: i,
          enumerable: true,
        });
      } else {
        const propertyKey =
          viewConfig.columnMapping[Number.parseInt(column.key)];
        let value = getExplicitValue(objectKey, propertyKey, data);
        Object.defineProperty(row, column.key, {
          value: value,
          enumerable: true,
        });
      }
    });
    rows.push(row);
  }
  return rows;
};

// For testing only
export function getRowMapping(
  sortColumns: SortColumn[] | undefined,
  viewConfig: TabularDataConfig,
  properties: PropertySet,
  unfilteredData: Data,
  variables?: { [key: string]: string },
  forcedOrder?: { [idx: number]: ObjectKey }
): (i: number) => ObjectKey | undefined {
  const data = !!viewConfig.rowFilter
    ? filterDictByValue(
        unfilteredData,
        filterRowUsingConfig(viewConfig.rowFilter, variables)
      )
    : unfilteredData;
  let index: ObjectKey[] = Object.keys(data);
  function compare(a: any, b: any, direction: SortDirection): number {
    const result = a > b ? 1 : a === b ? 0 : -1;
    return direction === "ASC" ? result : -result;
  }
  if (!!sortColumns && sortColumns[0]) {
    // Just support single column sorting for now
    const sortPropertyKey =
      viewConfig.columnMapping[parseInt(sortColumns[0].columnKey)];
    const direction = sortColumns[0].direction;
    if (sortPropertyKey in properties.descriptors) {
      const valuesToSort = Object.entries(data).map(([objectKey, row]) => [
        objectKey,
        row[sortPropertyKey],
      ]);

      valuesToSort.sort((a, b) => compare(a[1], b[1], direction));
      index = valuesToSort.map((val) => val[0]);
    }
    return (i) => index[i];
  } else if (!!forcedOrder) {
    return (i) => {
      const objectKey = forcedOrder[i];
      return objectKey in data ? objectKey : undefined;
    };
  }
  return (i) => index[i];
}

function rowKeyGetter(row: Row) {
  return row.rowId;
}

export type TabularConfigAtomType = WritableAtom<
  TabularDataConfig,
  TabularDataConfig | ((arg: TabularDataConfig) => TabularDataConfig),
  void
>;

export function TabularDataView({
  width,
  height,
  tabularConfigAtom,
  className,
  onRowsChange,
  onRowClick,
  selectedRows,
  renderRow,
  headerRenderer,
  enableVirtualization,
  fixedColumns = false,
  variables,
}: {
  width: number;
  height: number;
  tabularConfigAtom: TabularConfigAtomType;
  className?: string;
  onRowsChange?: (rows: Row[], changes: RowsChangeData<Row, Row>) => void;
  onRowClick?: (row: Row, column: DSColumn) => void;
  selectedRows?: Set<number>;
  renderRow?: (key: React.Key, props: RowRendererProps<Row, Row>) => ReactNode;
  headerRenderer?: HeaderRendererGenerator;
  enableVirtualization?: boolean;
  fixedColumns?: boolean;
  variables?: { [key: string]: string };
}) {
  // Atoms
  const config = useAtomValue(defaultsAtom);
  const setColumnWidth = useSetAtom(setColumnWidthAtom);
  const setStartRow = useSetAtom(startRowAtom);
  const [offsetColumns, setOffsetColumns] = useAtom(offsetColumnAtom);
  const [sortColumns, setSortColumns] = useAtom(sortColumnAtom);
  const viewConfig = useAtomValue(tabularConfigAtom);
  const datasets = useAtomValue(datasetsAtom);
  const cache = useAtomValue(dataCacheAtom);

  const startRow = useAtomValue(startRowAtom);
  // Derived constants
  if (!viewConfig.datasetId) {
    return <div>Dataset Not Yet Configured</div>;
  }
  const data = cache[viewConfig.datasetId] ?? {};
  const dataset = datasets[viewConfig.datasetId];
  const properties = dataset.properties;
  const columns = generateColumns(
    width,
    fixedColumns ? 0 : offsetColumns,
    fixedColumns,
    viewConfig,
    properties,
    config,
    headerRenderer
  );
  const rows = generateRows(
    height,
    startRow,
    columns,
    sortColumns,
    viewConfig,
    dataset,
    config,
    data,
    variables
  );

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop <=
      height
    ) {
      setStartRow((startRow) => startRow + 10);
    }
    if (
      event.currentTarget.scrollWidth - event.currentTarget.scrollLeft <=
      width
    ) {
      if (!fixedColumns) {
        setOffsetColumns((offsetColumn) => offsetColumn + 10);
      }
    }
  };

  const onColumnResize = (idx: number, width: number) => {
    setColumnWidth({ idx, width, tabularConfigAtom });
  };
  const onSortColumnsChange = (sortColumns: SortColumn[]) => {
    setSortColumns(sortColumns);
  };

  return (
    <DataGrid
      className={className}
      columns={columns}
      rows={rows}
      onRowsChange={onRowsChange}
      onRowClick={onRowClick}
      selectedRows={new Set<number>(selectedRows)}
      renderers={{ rowRenderer: renderRow }}
      enableVirtualization={enableVirtualization}
      onColumnResize={onColumnResize}
      onScroll={onScroll}
      onSortColumnsChange={onSortColumnsChange}
      sortColumns={sortColumns}
      rowKeyGetter={rowKeyGetter}
    />
  );
}
